import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
  useTable,
  useSortBy,
  useFilters,
  useGlobalFilter,
  Column,
  TableInstance,
  ColumnInstance,
  UseSortByColumnProps,
  UseSortByState,
  SortingRule,
  UseFiltersState,
  Filters,
} from 'react-table';
import { Parameter } from '../../types/SetpointManagerTypes';
import { DefaultColumnFilter } from '../data-tables/helpers/DefaultColumnFilter';
import { createMultiConditionFilter } from '../../utilities/tableFilters';
import './parameter-table.scss';
import { ReactComponent as ArrowUp } from '../../images/arrow-narrow-up.svg';
import { ReactComponent as ArrowDown } from '../../images/arrow-narrow-down.svg';
import { ReactComponent as ClockIcon } from '../../images/clock.svg';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { Checkbox } from '../../features/common/CustomCheckbox/CheckBox';
import EditableCell from './EditableCell';
import { ReactComponent as BellSolidRed } from '../../images/bell-solid-red.svg';
import { ReactComponent as BellSolidYellow } from '../../images/bell-solid-yellow.svg';
import { ReactComponent as BellIcon } from '../../images/bell-02.svg';
import { UrlParams } from '../../features/data-history/TrendLibrary/ChartTypes';
import { useParams } from 'react-router-dom';
import SimpleModal from '../common/simple-modal/SimpleModal';
import SetpointHistory from '../../features/setpoints-manager/setpoint-history/SetpointHistory';
import { useAppDispatch } from '../../hooks/storeHooks';
import { setSelectedRows } from '../../features/setpoints-manager/parameterGroupsSlice';
import { ProtectAction } from '../../rbac';
import Helpers from '../../utilities/Helpers';
import { formatDate } from '../../utilities/dateHelpers';

interface ParameterTableProps {
  parameters: Parameter[];
  showBackup: boolean;
  onFilteredCountChange?: (count: number) => void; // NEW PROP
  updateSortData: (sortData: SortingRule<Parameter>[]) => void;
  sortData: SortingRule<Parameter>[];
  updateFilters: (filters: Filters<Parameter>) => void;
  filters: Filters<Parameter>;
  rPermission: { [key: string]: boolean };
}

const ParameterTable: React.FC<ParameterTableProps> = ({
  parameters,
  showBackup,
  onFilteredCountChange,
  updateSortData,
  sortData,
  updateFilters,
  filters,
  rPermission,
}) => {
  const dispatch = useAppDispatch();
  const { assetId } = useParams<keyof UrlParams>() as UrlParams;
  const [currentFilters, setCurrentFilters] = useState<any[]>([]);
  const currentFiltersRef = useRef(currentFilters);
  const globalFilterValue = useSelector((state: RootState) => state.globalFilter.value);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [historyParameter, setHistoryParameter] = useState<string | undefined>(undefined);
  const selectedRowsArray = useSelector((state: RootState) => state.parameterGroups.selectedRows);
  const selectedRows = useMemo(() => new Set<string>(selectedRowsArray), [selectedRowsArray]);

  useEffect(() => {
    currentFiltersRef.current = currentFilters; // Keep the ref updated with the latest value of currentFilters
  }, [currentFilters]);

  const multiConditionFilter = createMultiConditionFilter(setCurrentFilters, currentFiltersRef);

  // Shared sort function for numeric/string columns
  const numericStringSort = (rowA: any, rowB: any, columnId: string) => {
    const valA = rowA.values[columnId];
    const valB = rowB.values[columnId];
    const numA = parseFloat(valA);
    const numB = parseFloat(valB);
    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }
    return String(valA).localeCompare(String(valB));
  };

  const clearFilterForColumn = (columnId: string) => {
    const remainingFilters = currentFiltersRef.current.filter((filter) => filter.columnId !== columnId);
    //setCurrentFilters(remainingFilters);

    if (remainingFilters.length === 0) {
      setAllFilters([]);
    } else {
      setAllFilters(
        remainingFilters.map((filter) => ({
          id: filter.columnId,
          value: filter.filterValue,
        })),
      );
    }
  };

  const handleHistoryClick = (parameter: Parameter) => {
    setHistoryParameter(parameter.parameter);
    setIsModalOpen(true);
  };

  const defaultColumn: any = React.useMemo(
    () => ({
      // Set up our default Filter UI
      Filter: (props: any) => (
        <DefaultColumnFilter
          {...props}
          clearFilterForColumn={clearFilterForColumn}
          currentFiltersRef={currentFiltersRef}
          setAllFilters={setAllFilters}
        />
      ),
    }),
    [currentFiltersRef], // Only depend on currentFiltersRef, not currentFilters
  );

  const getAlarmIcon = (parameter: Parameter): React.ComponentType | null => {
    if (!parameter.hasAlarm) return null;

    if (!parameter.alarmType) return BellIcon;

    switch (parameter.alarmType) {
      case 1:
        return BellSolidRed;
      case 2:
        return BellSolidYellow;
      default:
        return null;
    }
  };

  const handleSelectAll = () => {
    const selectRows = new Set(selectedRows);
    if (!isAllSelected) {
      parameters.forEach((param) => {
        selectRows.add(param.parameter);
      });
      dispatch(setSelectedRows(Array.from(selectRows)));
    } else {
      parameters.forEach((param) => {
        selectRows.delete(param.parameter);
      });
      dispatch(setSelectedRows(Array.from(selectRows)));
    }
  };

  const handleRowSelect = (rowId: string) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(rowId)) {
      newSelectedRows.delete(rowId);
    } else {
      newSelectedRows.add(rowId);
    }
    dispatch(setSelectedRows(Array.from(newSelectedRows)));
  };
  const selectedParams = new Set(
    [...selectedRows].filter((num) => parameters.some((param) => param.parameter === num)),
  );
  const isAllSelected = selectedParams.size === parameters.length && parameters.length > 0;

  const columns: Column<Parameter>[] = useMemo(() => {
    const baseColumns: Column<Parameter>[] = [
      {
        Header: () => (
          <div className={`parameter-table__checkbox`}>
            <Checkbox isChecked={isAllSelected} Label='' onItemSelect={handleSelectAll} />
          </div>
        ),
        accessor: 'parameter' as const,
        id: 'select',
        Cell: ({ value }: { value: string }) => (
          <div className={`parameter-table__checkbox`}>
            <Checkbox isChecked={selectedRows.has(value)} Label='' onItemSelect={() => handleRowSelect(value)} />
          </div>
        ),
        disableSortBy: true,
      } as any,
      {
        Header: () => (
          <div className='parameter-table__header-content'>
            <div className='parameter-table__header-row'>
              <span className='parameter-table__header-text'>Parameter</span>
            </div>
          </div>
        ),
        accessor: 'parameter' as const,
        Cell: ({ value }: { value: string }) => <span className='parameter-table__data-text'>{value}</span>,
        filter: multiConditionFilter,
      } as any,
      {
        Header: () => (
          <div className='parameter-table__header-content'>
            <div className='parameter-table__header-row'>
              <span className='parameter-table__header-text'>Description</span>
            </div>
          </div>
        ),
        accessor: 'description' as const,
        Cell: ({ value }: { value: string }) => <span className='parameter-table__data-text'>{Helpers.toSentenceCase(value)}</span>,
        filter: multiConditionFilter,
      } as any,
      {
        Header: () => (
          <div className='parameter-table__header-content'>
            <div className='parameter-table__header-row'>
              <span className='parameter-table__header-text'>Last scan time</span>
            </div>
          </div>
        ),
        accessor: 'lastScanDate' as const,
        Cell: ({ value }: { value: string }) => <span className='parameter-table__data-text'>{formatDate(value, true)}</span>,
        filter: multiConditionFilter,
        sortType: (rowA: any, rowB: any, columnId: string) => {
          const dateA = new Date(rowA.values[columnId]);
          const dateB = new Date(rowB.values[columnId]);

          if (dateA < dateB) return -1;
          if (dateA > dateB) return 1;
          return 0;
        },
      } as any,
      {
        Header: () => (
          <div className='parameter-table__header-content'>
            <div className='parameter-table__header-row'>
              <span className='parameter-table__header-text'></span>
            </div>
          </div>
        ),
        accessor: 'alarmIcon' as const,
        Cell: ({ row }: { row: { original: Parameter } }) => {
          const AlarmIconComponent = getAlarmIcon(row.original);
          return (
            <div className='parameter-table__alarm-icon'>
              <div className='sp-bell-icon'>{AlarmIconComponent && <AlarmIconComponent />}</div>
            </div>
          );
        },
        disableSortBy: true,
        canFilter: false,
      } as any,
      {
        Header: () => (
          <div className='parameter-table__header-content'>
            <div className='parameter-table__header-row'>
              <span className='parameter-table__header-text'>Controller value</span>
            </div>
          </div>
        ),
        accessor: 'currentValue' as const,
        Cell: ({ row }: { row: { original: Parameter } }) => {
          return <EditableCell parameter={row.original} />;
        },
        filter: multiConditionFilter,
        sortType: (rowA: any, rowB: any, columnId: string) => {
          return numericStringSort(rowA, rowB, columnId);
        },
      } as any,
      {
        Header: () => null,
        accessor: 'id' as const,
        id: 'actions',
        Cell: ({ row }: { row: { original: Parameter } }) => (
          <ProtectAction canAccess={rPermission.canViewSetpointHistory}>
            <div className='action-icon' style={{ cursor: 'pointer' }} onClick={() => handleHistoryClick(row.original)}>
              <ClockIcon />
            </div>
          </ProtectAction>
        ),
        disableSortBy: true,
      } as any,
    ];

    // Add backup columns when showBackup is true
    if (showBackup) {
      baseColumns.push(
        {
          Header: () => (
            <div className='parameter-table__header-content'>
              <div className='parameter-table__header-row'>
                <span className='parameter-table__header-text'>Backup value</span>
              </div>
            </div>
          ),
          accessor: 'backupValue' as const,
          id: 'backupValueColumn',
          Cell: ({ value }: { value: string }) => <span className='parameter-table__data-text'>{value}</span>,
          filter: multiConditionFilter,
          sortType: (rowA: any, rowB: any, columnId: string) => {
            return numericStringSort(rowA, rowB, columnId);
          },
        } as any,
        {
          Header: () => (
            <div className='parameter-table__header-content'>
              <div className='parameter-table__header-row'>
                <span className='parameter-table__header-text'>Last backup time</span>
              </div>
            </div>
          ),
          accessor: 'backupDate' as const,
          id: 'backupDateColumn',
          Cell: ({ value }: { value: string }) => <span className='parameter-table__data-text'>{formatDate(value, true)}</span>,
          filter: multiConditionFilter,
          sortType: (rowA: any, rowB: any, columnId: string) => {
            const dateA = new Date(rowA.values[columnId]);
            const dateB = new Date(rowB.values[columnId]);

            if (dateA < dateB) return -1;
            if (dateA > dateB) return 1;
            return 0;
          },
        } as any,
      );
    }

    return baseColumns;
  }, [selectedRows, isAllSelected, parameters.length, showBackup]);

  const instance = useTable(
    {
      columns,
      data: parameters,
      defaultColumn: defaultColumn,
      initialState: {
        sortBy: sortData,
        filters: filters,
      } as any,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setAllFilters, setGlobalFilter, state } =
    instance as TableInstance<Parameter> & {
      setGlobalFilter: (filterValue: string | undefined) => void;
      setAllFilters: (filters: any[]) => void;
      state: (typeof instance)['state'] & UseSortByState<Parameter> & UseFiltersState<Parameter>;
    };
  useEffect(() => {
    updateSortData(state.sortBy);
  }, [state.sortBy]);

  useEffect(() => {
    updateFilters(state.filters);
  }, [state.filters]);
  useEffect(() => {
    setGlobalFilter(globalFilterValue);
  }, [globalFilterValue, setGlobalFilter]);
  useEffect(() => {
    if (onFilteredCountChange) {
      onFilteredCountChange(rows.length);
    }
  }, [rows.length, onFilteredCountChange]);

  return (
    <div>
      <section
        className={`parameter-table ${showBackup ? 'parameter-table--with-backup' : 'parameter-table--without-backup'}`}
        {...getTableProps()}
      >
        {headerGroups.map((headerGroup) => (
          <div className='parameter-table__header-row-container' {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((col) => {
              const column = col as unknown as ColumnInstance<Parameter> & UseSortByColumnProps<Parameter> as any;
              return (
                <div
                  className={`parameter-table__column parameter-table__column--${column.id}`}
                  {...column.getHeaderProps(column.getSortByToggleProps ? column.getSortByToggleProps() : {})}
                >
                  <div
                    className={`parameter-table__header-cell ${column.id === 'select' || column.id === 'backupValueColumn'
                      ? 'parameter-table__header-cell--center'
                      : column.id === 'currentValue' || column.id === 'backupValue'
                        ? 'parameter-table__header-cell--end'
                        : ''
                      }`}
                  >
                    <div className='flex items-center'>
                      {column.render('Header')}
                      <div>{column.isSorted ? column.isSortedDesc ? <ArrowDown /> : <ArrowUp /> : ''}</div>
                      <span>
                        {column.canFilter &&
                          column.id !== 'alarmIcon' &&
                          column.id !== 'select' &&
                          column.id !== 'actions'
                          ? column.render('Filter')
                          : null}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        <div className='parameter-table__body' {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <div className='parameter-table__data-row' {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <div
                    className={`parameter-table__column parameter-table__column--${cell.column.id}`}
                    {...cell.getCellProps()}
                  >
                    <div
                      className={`parameter-table__data-cell ${cell.column.id === 'select'
                        ? 'parameter-table__data-cell--center'
                        : cell.column.id === 'backupValue' ||
                          cell.column.id === 'backupValueColumn' ||
                          cell.column.id === 'currentValue' ||
                          cell.column.id === 'actions'
                          ? 'parameter-table__data-cell--end'
                          : ''
                        }`}
                    >
                      {cell.render('Cell')}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </section>
      {isModalOpen && historyParameter && (
        <SimpleModal header={`Parameter history - ${historyParameter}`} onClose={() => setIsModalOpen(false)}>
          <SetpointHistory assetId={assetId} parameterName={historyParameter} rPermission={rPermission} />
        </SimpleModal>
      )}
    </div>
  );
};

export default ParameterTable;
